import { ThemedText } from '@/components/themed-text';
import { joinFirstNames } from '@/components/team/member-avatar';
import { CatalogItemSheet } from '@/components/services/catalog-item-sheet';
import { PressableScale } from '@/components/ui/pressable-scale';
import { CatalogItem } from '@/types/service-catalog';
import { formatCurrency } from '@/utils/format-currency';
import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { View, type ViewStyle } from 'react-native';

const cardStyle: ViewStyle = {
  borderCurve: 'continuous',
  boxShadow: '0 1px 2px rgba(23, 17, 10, 0.04), 0 4px 12px rgba(23, 17, 10, 0.05)',
};

/**
 * O serviço como o cliente o vê: um card por serviço do negócio, com quem faz
 * e o menor preço. Tocar abre a folha — o dono edita a identidade e quem faz,
 * o profissional só diz se também faz. A cópia de cada um continua em "Meus".
 */
export function CatalogItemCard({ item }: { item: CatalogItem }) {
  const [open, setOpen] = useState(false);
  const prices = item.professionals
    .map((professional) => professional.price)
    .filter((price): price is number => price != null);
  const priceLabel = prices.length
    ? `a partir de ${formatCurrency(Math.min(...prices))}`
    : 'A combinar';
  const whoLabel = item.professionals.length
    ? joinFirstNames(item.professionals.map((professional) => professional.name))
    : 'Ninguém faz ainda';

  return (
    <>
      <PressableScale
        haptic
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        className="mb-2 w-full flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 bg-card px-4 py-3.5"
        style={cardStyle}
      >
        <View className="h-10 w-10 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary">
          <Ionicons className="text-primary dark:text-white" name="pricetags-outline" size={18} />
        </View>
        <View className="flex-1 gap-0.5">
          <ThemedText className="font-semibold text-card-foreground" numberOfLines={1}>
            {item.name}
          </ThemedText>
          <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
            {whoLabel} · {priceLabel}
          </ThemedText>
        </View>
        {item.mine ? (
          <View className="rounded-full bg-primary-50 px-2.5 py-1">
            <ThemedText className="font-medium text-[11px] text-primary-600">Eu faço</ThemedText>
          </View>
        ) : null}
        <Ionicons className="text-neutral-300" name="chevron-forward" size={16} />
      </PressableScale>
      <CatalogItemSheet item={item} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
